import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";

import { useHttpClient } from "shared/hooks/http-hook";
import SmsSenderAccountSelect, { DEFAULT_SMS_MSG_HEADER } from "shared/components/SmsSenderAccountSelect";
import { isInternationalTheme } from "config/ConfigUtils";

const MAX_SMS_LENGTH = 1000;

const BulkSmsPanel = () => {
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();

  const [rawInput, setRawInput] = useState("");
  const [validatedPhones, setValidatedPhones] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [msgHeader, setMsgHeader] = useState(DEFAULT_SMS_MSG_HEADER);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const parsePhoneNumbers = useCallback((input) => {
    return input
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, []);

  const handleValidate = useCallback(async () => {
    const numbers = parsePhoneNumbers(rawInput);
    if (numbers.length === 0) return;

    setIsValidating(true);
    setIsValidated(false);
    setSendResult(null);

    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2/validate-phones`,
        "POST",
        JSON.stringify({ phoneNumbers: numbers }),
      );
      setValidatedPhones(responseData.results);
      setIsValidated(true);
    } catch (err) {
      console.error(err);
    }
    setIsValidating(false);
  }, [rawInput, parsePhoneNumbers, sendRequest]);

  const handleRemovePhone = useCallback((index) => {
    setValidatedPhones((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validCount = validatedPhones.filter((p) => p.isValid).length;
  const duplicateCount = validatedPhones.filter((p) => p.isDuplicate).length;
  const invalidCount = validatedPhones.filter((p) => !p.isValid && !p.isDuplicate).length;

  const handleSendConfirm = useCallback(() => {
    if (validCount === 0 || messageText.trim().length === 0) return;
    setConfirmOpen(true);
  }, [validCount, messageText]);

  const handleSend = useCallback(async () => {
    setConfirmOpen(false);
    setIsSending(true);
    setSendResult(null);

    const numbersToSend = validatedPhones.filter((p) => p.isValid).map((p) => p.normalized);

    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2/bulk`,
        "POST",
        JSON.stringify({
          phoneNumbers: numbersToSend,
          text: messageText.trim(),
          ...(!isInternationalTheme() && { msgHeader }),
        }),
      );
      setSendResult(responseData);
    } catch (err) {
      console.error(err);
    }
    setIsSending(false);
  }, [validatedPhones, messageText, msgHeader, sendRequest]);

  return (
    <Box sx={{ py: 3, px: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: "var(--color-solid)" }}>
        {t("bulkSms")}
      </Typography>

      <Box sx={{ display: "flex", gap: 3, flex: 1, minHeight: 0 }}>
        {/* Left column - Phone numbers input */}
        <Paper
          sx={{ p: 3, borderRadius: 2, width: "40%", display: "flex", flexDirection: "column", flexShrink: 0 }}
          elevation={1}
        >
          <TextField
            multiline
            fullWidth
            value={rawInput}
            onChange={(e) => {
              setRawInput(e.target.value);
              setIsValidated(false);
              setSendResult(null);
            }}
            placeholder={t("phoneNumbersPlaceholder")}
            sx={{
              flex: 1,
              mb: 2,
              backgroundColor: "#fff",
              "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" },
              "& .MuiInputBase-inputMultiline": { height: "100% !important", overflow: "auto !important" },
            }}
          />

          <Button
            variant="contained"
            fullWidth
            startIcon={isValidating ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            disabled={isValidating || rawInput.trim().length === 0}
            onClick={handleValidate}
            sx={{
              textTransform: "none",
              backgroundColor: "var(--color-link)",
              "&:hover": { backgroundColor: "var(--color-primary-surface)" },
              py: 1.2,
            }}
          >
            {t("validatePhoneNumbers")}
          </Button>
        </Paper>

        {/* Right column - Results, message, send */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>
          {/* Summary */}
          {isValidated && (
            <Paper sx={{ px: 3, py: 1.5, borderRadius: 2 }} elevation={1}>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {validatedPhones.length} {t("numbersEntered")} —{" "}
                <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                  {validCount} {t("validNumbers")}
                </span>
                {duplicateCount > 0 && (
                  <>
                    ,{" "}
                    <span style={{ color: "#e65100", fontWeight: 600 }}>
                      {duplicateCount} {t("duplicateNumber")}
                    </span>
                  </>
                )}
                {invalidCount > 0 && (
                  <>
                    ,{" "}
                    <span style={{ color: "#d32f2f", fontWeight: 600 }}>
                      {invalidCount} {t("invalidNumbers")}
                    </span>
                  </>
                )}
              </Typography>
            </Paper>
          )}

          {/* Validated phone numbers table */}
          {isValidated && validatedPhones.length > 0 && (
            <Paper sx={{ borderRadius: 2, flex: 1, minHeight: 0, overflow: "hidden" }} elevation={1}>
              <TableContainer sx={{ maxHeight: "100%" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("phoneNumber")}</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("status")}</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                        {t("registrationStatus")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }} align="right">
                        {t("actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validatedPhones.map((phone, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: phone.isDuplicate
                            ? "rgba(230, 81, 0, 0.04)"
                            : !phone.isValid
                              ? "rgba(211, 47, 47, 0.04)"
                              : "inherit",
                          "&:hover": {
                            backgroundColor: phone.isDuplicate
                              ? "rgba(230, 81, 0, 0.08)"
                              : !phone.isValid
                                ? "rgba(211, 47, 47, 0.08)"
                                : "#f5f5f5",
                          },
                        }}
                      >
                        <TableCell sx={{ color: "#999", width: 40 }}>{index + 1}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {phone.normalized || phone.original}
                            </Typography>
                            {phone.normalized && phone.normalized !== phone.original && (
                              <Typography variant="caption" sx={{ color: "#999" }}>
                                {phone.original}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {phone.isValid ? (
                            <Chip
                              icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                              label={t("validNumbers")}
                              size="small"
                              sx={{
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 500,
                                "& .MuiChip-icon": { color: "#2e7d32" },
                              }}
                            />
                          ) : phone.isDuplicate ? (
                            <Chip
                              icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                              label={t("duplicateNumber")}
                              size="small"
                              sx={{
                                backgroundColor: "#fff3e0",
                                color: "#e65100",
                                fontWeight: 500,
                                "& .MuiChip-icon": { color: "#e65100" },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                              label={t("invalidNumbers")}
                              size="small"
                              sx={{
                                backgroundColor: "#ffebee",
                                color: "#d32f2f",
                                fontWeight: 500,
                                "& .MuiChip-icon": { color: "#d32f2f" },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {phone.isValid &&
                            (phone.isRegistered ? (
                              <Chip
                                label={`${t("registered")}${phone.userName ? ` — ${phone.userName}` : ""}`}
                                size="small"
                                sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 500 }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: "#999" }}>
                                {t("notRegistered")}
                              </Typography>
                            ))}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleRemovePhone(index)} sx={{ color: "#999" }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Message input and send */}
          {isValidated && validCount > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2, flexShrink: 0 }} elevation={1}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: "var(--color-solid)" }}>
                {t("messageText")}
              </Typography>

              {!isInternationalTheme() && (
                <SmsSenderAccountSelect
                  value={msgHeader}
                  onChange={setMsgHeader}
                  disabled={isSending}
                  sx={{ mb: 2, maxWidth: 360 }}
                />
              )}

              <TextField
                multiline
                minRows={3}
                maxRows={5}
                fullWidth
                value={messageText}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_SMS_LENGTH) {
                    setMessageText(e.target.value);
                  }
                }}
                placeholder={t("messageTextPlaceholder")}
                inputProps={{ maxLength: MAX_SMS_LENGTH }}
                sx={{ mb: 1, backgroundColor: "#fff" }}
              />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" sx={{ color: messageText.length >= MAX_SMS_LENGTH ? "#d32f2f" : "#999" }}>
                  {messageText.length}/{MAX_SMS_LENGTH} {t("smsCharacterCount")}
                </Typography>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={isSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  disabled={isSending || messageText.trim().length === 0}
                  onClick={handleSendConfirm}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {isSending ? t("bulkSmsSending") : t("sendBulkSmsButton")}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Send result */}
          {sendResult && (
            <Alert severity={sendResult.totalFailed === 0 ? "success" : "warning"} sx={{ borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t("bulkSmsResult")}
              </Typography>
              <Typography variant="body2">
                {sendResult.totalSent} {t("smsSentSuccess")}
                {sendResult.totalFailed > 0 && `, ${sendResult.totalFailed} ${t("smsSentFailed")}`}
              </Typography>
            </Alert>
          )}
        </Box>
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t("bulkSms")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmBulkSms", { count: validCount })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSend} color="error" variant="contained" sx={{ textTransform: "none" }}>
            {t("sendBulkSmsButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkSmsPanel;
